import { useState, useEffect, useCallback, useRef } from "react";
import { UpdateIPCService } from "@/shared/services/UpdateIPCService";
import type { UpdateStatus } from "@/shared/types/update";

interface UseUpdateOptions {
  autoCheck?: boolean;
  checkInterval?: number; // в миллисекундах
  onUpdateAvailable?: (version: string) => void;
  onUpdateDownloaded?: () => void;
  onError?: (error: string) => void;
}

export const useUpdate = (options: UseUpdateOptions = {}) => {
  const {
    autoCheck = true,
    checkInterval = 30 * 60 * 1000, // 30 минут по умолчанию
    onUpdateAvailable,
    onUpdateDownloaded,
    onError,
  } = options;

  const [status, setStatus] = useState<UpdateStatus>({ status: "idle" });
  const [isChecking, setIsChecking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Проверка доступности Electron API
  const isElectronAvailable = UpdateIPCService.isAvailable();

  // Загрузка текущего статуса
  const loadStatus = useCallback(async () => {
    if (!isElectronAvailable) return;

    try {
      const currentStatus = await UpdateIPCService.getUpdateStatus();
      setStatus(currentStatus);
    } catch (error) {
      console.error("Error loading update status:", error);
    }
  }, [isElectronAvailable]);

  // Проверка обновлений
  const checkForUpdates = useCallback(async () => {
    if (!isElectronAvailable) {
      console.warn("Electron API not available");
      return;
    }

    setIsChecking(true);

    try {
      const result = await UpdateIPCService.checkForUpdates();

      if (result.updateAvailable && result.version) {
        setIsVisible(true);
        onUpdateAvailable?.(result.version);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("Error checking for updates:", error);
      setStatus({
        status: "error",
        error: errorMessage,
      });
      onError?.(errorMessage);
    } finally {
      setIsChecking(false);
    }
  }, [isElectronAvailable, onUpdateAvailable, onError]);

  // Запуск обновления
  const startUpdate = useCallback(async () => {
    if (!isElectronAvailable) return;

    try {
      await UpdateIPCService.startUpdate();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("Error starting update:", error);
      setStatus({
        status: "error",
        error: errorMessage,
      });
      onError?.(errorMessage);
    }
  }, [isElectronAvailable, onError]);

  // Подписка на изменения статуса
  useEffect(() => {
    if (!isElectronAvailable) return;

    // Загружаем начальный статус
    loadStatus();

    // Подписываемся на изменения
    unsubscribeRef.current = UpdateIPCService.onUpdateStatusChange(
      (newStatus) => {
        setStatus(newStatus);

        if (newStatus.status === "update-available") {
          setIsVisible(true);
        }

        if (newStatus.status === "downloaded") {
          setIsVisible(true);
          onUpdateDownloaded?.();
        }
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [isElectronAvailable, loadStatus, onUpdateDownloaded]);

  // Автоматическая проверка при монтировании
  useEffect(() => {
    if (autoCheck && isElectronAvailable) {
      checkForUpdates();
    }
  }, [autoCheck, isElectronAvailable, checkForUpdates]);

  // Периодическая проверка
  useEffect(() => {
    if (!autoCheck || !isElectronAvailable || checkInterval <= 0) return;

    intervalRef.current = setInterval(() => {
      checkForUpdates();
    }, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoCheck, checkInterval, isElectronAvailable, checkForUpdates]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const closeWindow = useCallback(() => {
    setIsVisible(false);
  }, []);

  const hasUpdate =
    status.status === "update-available" || status.status === "downloaded";

  return {
    status,
    isChecking,
    isVisible,
    hasUpdate,
    isElectronAvailable,
    checkForUpdates,
    startUpdate,
    closeWindow,
  };
};
